import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "../../../../parts/avatar";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import {
  buildSupersets,
  calculatePrefixForExercise,
  groupExercisesBySection,
} from "../../../../utils/prefix-calculator";
import { splitSections } from "../../../../utils/section-types";
import WorkoutNoteBlock from "./EditMode/ExerciseEditor/WorkoutNoteBlock";
import WeekDayExercise from "./WeekDayExercise";

const {
  DATE_FORMATS: { DATE_DAY_FORMAT },
} = require("../../../../constants");

const WeekDayModal = ({ date, onClose, weekStart }) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const {
    activeWeek,
    athlete,
    coachOrganizationInformation,
    scheduledSessions = {},
    scheduledSections = {},
    workoutNotes,
    workoutCoachNotes,
  } = locals;

  const weekDays = Array.from({ length: 7 }, (_, i) =>
    (weekStart || activeWeek).clone().add(i, "days")
  );
  const initialIdx = weekDays.findIndex((d) => d.isSame(date, "day"));
  const [activeDayIdx, setActiveDayIdx] = useState(
    initialIdx >= 0 ? initialIdx : 0
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft")
        setActiveDayIdx((prev) => Math.max(0, prev - 1));
      if (e.key === "ArrowRight")
        setActiveDayIdx((prev) => Math.min(6, prev + 1));
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const activeDay = weekDays[activeDayIdx];
  const dayString = activeDay.format(DATE_DAY_FORMAT);
  const exercises = scheduledSessions[dayString] || [];
  const sections = scheduledSections[dayString] || [];

  const note = workoutNotes?.notes?.find(
    (n) =>
      activeDay.format("YYYY-MM-DD") === moment.utc(n.date).format("YYYY-MM-DD")
  );
  const coachNote = workoutCoachNotes?.notes?.find(
    (n) =>
      activeDay.format("YYYY-MM-DD") === moment.utc(n.date).format("YYYY-MM-DD")
  );

  const supersets = exercises.length ? buildSupersets(exercises) : [];
  const sectionGroups = groupExercisesBySection(exercises, sections);
  // `workoutDayNote` to avoid colliding with `note` above (the athlete's own
  // post-workout note, an unrelated feature on a different collection).
  const { note: workoutDayNote } = splitSections(sections);

  return (
    <div className="wk-modal-overlay open" onClick={onClose}>
      <div className="wk-modal" onClick={(e) => e.stopPropagation()}>
        <header className="wk-modal-header">
          <h2 className="alt">
            {t("calendar.workoutDetails", "Workout Details")}
          </h2>
          <button
            type="button"
            className="wk-modal-close"
            onClick={onClose}
            aria-label={t("calendar.close", "Close")}
          >
            <img
              className="icon-red"
              src="/img/new-icons/close-modal.svg"
              alt="dismiss"
            />
          </button>
        </header>
        <div className="wk-modal-top">
          <div className="wk-modal-tabs">
            {weekDays.map((day, idx) => {
              const hasExercises =
                (scheduledSessions[day.format(DATE_DAY_FORMAT)] || []).length >
                0;
              return (
                <button
                  key={day.format(DATE_DAY_FORMAT)}
                  type="button"
                  sz="small"
                  data-button-type={
                    idx === activeDayIdx ? "outline" : "secondary"
                  }
                  className={!hasExercises ? "is-rest" : ""}
                  onClick={() => setActiveDayIdx(idx)}
                >
                  {day.format("ddd")} {day.format("D")}
                </button>
              );
            })}
          </div>
        </div>

        <div className="wk-modal-body">
          <WorkoutNoteBlock note={workoutDayNote} editable={false} />
          {exercises.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 0",
                color: "var(--white-30)",
                fontSize: "var(--font-size-sm)",
              }}
            >
              {t("calendar.restDay", "Rest day")}
            </div>
          ) : (
            sectionGroups.map(({ section, exercises: secExs }) => {
              if (!secExs.length) return null;
              return (
                <div key={section?._id || "unsectioned"}>
                  {section && (
                    <div className="wk-section-label">{section.name}</div>
                  )}
                  {secExs.map((ex) => {
                    const prefix = calculatePrefixForExercise(
                      ex._id,
                      supersets
                    );
                    return (
                      <WeekDayExercise
                        key={ex._id}
                        exercise={ex}
                        prefix={prefix}
                      />
                    );
                  })}
                </div>
              );
            })
          )}

          {note?.note && (
            <div className="wk-day-note">
              <Avatar className="wk-day-note-avatar" user={athlete} size={20} />
              <span>{note.note}</span>
            </div>
          )}
          {coachNote?.note && (
            <div className="wk-coach-note">
              <Avatar
                className="wk-coach-avatar"
                user={coachOrganizationInformation}
                size={20}
              />
              <span>{coachNote.note}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeekDayModal;
